// Delayed actions (qa-runs#1171, the table half; docs/MRCS/shadowrun-3.md §18 "Delay"): on their turn a combatant
// presses Delay, steps out of the order and is listed as waiting; Act now brings them in before whoever is due. How
// long they may wait and whether acting moves their place is the system's rule, as data (`features.delay`):
//
//   until        'pass'      the wait ends with the initiative pass (or the round, with no passes) it began in;
//                'own_turn'  the wait ends when the order comes round to them again — they take that turn as usual,
//                            and the delayed action is lost;
//   takes_place  true        acting moves them to just before the one who was due, and that is their place (and
//                            their initiative) for the rounds after; false: they keep their score and place.
//
// The table holds no system's choice: a system with no `features.delay` has no Delay button.
//
// Pure. The waiting list lives on the encounter (`waiting`), as `passes` does; it is the DM's whole, and a player is
// sent only the waiting combatants they can see (`waitingSeen`) — hidden means absent.
import { advanceTurn, currentTurn, turnKey } from './encounter.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** `features.delay` as the table runs it; null when the system has none, or it is malformed. */
export function parseDelayRule(v) {
    if (!isObj(v) || (v.until !== 'pass' && v.until !== 'own_turn') || typeof v.takes_place !== 'boolean')
        return null;
    const label = typeof v.label === 'string' && v.label.trim() && v.label.length <= 40 ? v.label.trim() : 'Delay';
    return { until: v.until, takesPlace: v.takes_place, label };
}
export const NOT_WAITING = "That combatant isn't waiting.";
export const CANNOT_DELAY = 'Only the combatant whose turn it is can delay.';
export const NO_DELAY_RULE = "This table's game system has no delayed action.";
export const DELAY_KINDS = ['encounter.delay', 'encounter.act', 'encounter.waitLost'];
/** A stamped rule as stored on an Act now (`stampDelay`), strictly. */
function storedRule(v) {
    if (!isObj(v) || (v.until !== 'pass' && v.until !== 'own_turn') || typeof v.takesPlace !== 'boolean' || typeof v.label !== 'string' || v.label.length > 40)
        return null;
    return { until: v.until, takesPlace: v.takesPlace, label: v.label };
}
/**
 * A body, strictly. The server's stamps (an Act now's `rule`, the waits lost) are read back for the replay of a stored
 * record; from the wire they never stand: the server stamps `rule` over whatever came, and `encounter.waitLost` is a kind
 * only the server commits (the server's SYSTEM_KINDS).
 */
export function parseDelayBody(b) {
    if (b.kind === 'encounter.waitLost') {
        return Array.isArray(b.ids) && b.ids.length >= 1 && b.ids.length <= 200 && b.ids.every((x) => typeof x === 'string' && UUID_RE.test(x)) ? { kind: 'encounter.waitLost', ids: [...b.ids] } : null;
    }
    if (b.kind === 'encounter.act' && b.rule !== undefined) {
        const rule = storedRule(b.rule);
        return typeof b.id === 'string' && UUID_RE.test(b.id) && rule ? { kind: 'encounter.act', id: b.id, rule } : null;
    }
    if (b.kind === 'encounter.delay') {
        const t = isObj(b.turn) ? b.turn : null;
        if (typeof b.id !== 'string' || !UUID_RE.test(b.id) || !t || typeof t.round !== 'number' || typeof t.active !== 'string')
            return null;
        return { kind: 'encounter.delay', id: b.id, turn: { round: t.round, active: t.active, ...(typeof t.pass === 'number' ? { pass: t.pass } : {}) } };
    }
    if (b.kind === 'encounter.act')
        return typeof b.id === 'string' && UUID_RE.test(b.id) ? { kind: 'encounter.act', id: b.id } : null;
    return null;
}
/** Why a Delay is refused now, or null. */
export function delayRefusal(enc, body, rule) {
    if (!rule)
        return NO_DELAY_RULE;
    const now = currentTurn(enc);
    if (!now || turnKey(now) !== turnKey(body.turn) || now.active !== body.id)
        return CANNOT_DELAY;
    if ((enc.waiting ?? []).some((w) => w.id === body.id))
        return CANNOT_DELAY;
    return null;
}
/** Why an Act now is refused now, or null. */
export function actRefusal(enc, id, rule) {
    if (!rule)
        return NO_DELAY_RULE;
    if (!enc.running || !(enc.waiting ?? []).some((w) => w.id === id))
        return NOT_WAITING;
    return null;
}
/** Delay: the active combatant joins the waiting list, and the turn passes on as an End turn passes it. */
export function delayTurn(enc, id) {
    const w = { id, round: enc.round, ...(enc.passes ? { pass: enc.passes.pass } : {}) };
    const next = advanceTurn(enc);
    return { ...next, waiting: [...(enc.waiting ?? []), w] };
}
/**
 * Act now: the waiting combatant takes the turn before whoever is due. With `takesPlace` it moves in the order to just
 * before them and takes their initiative (their place for the rounds after); otherwise its place and score stay, and
 * when it ends its turn the order goes back to the one it came in before.
 */
export function actNow(enc, id, rule) {
    const waiting = (enc.waiting ?? []).filter((w) => w.id !== id);
    const from = enc.combatants.findIndex((c) => c.id === id);
    if (from < 0)
        return { ...enc, waiting };
    const { turn: _turn, ...base } = enc;
    if (!rule.takesPlace)
        return { ...base, activeIndex: from, waiting };
    const due = enc.combatants[enc.activeIndex];
    const list = enc.combatants.filter((c) => c.id !== id);
    const at = due ? list.findIndex((c) => c.id === due.id) : list.length;
    const moved = { ...enc.combatants[from], ...(due ? { initRoll: due.initRoll } : {}) };
    delete moved.tie;
    list.splice(at < 0 ? list.length : at, 0, moved);
    return { ...base, combatants: list, activeIndex: at < 0 ? list.length - 1 : at, waiting };
}
/**
 * The waits that run out as the order moves from `before` to `after`: by the pass (or the round) under `until: 'pass'`;
 * under 'own_turn' when the order reaches the waiting combatant again. Returns the encounter without them and who lost.
 */
export function expireWaiting(after, rule) {
    const waiting = after.waiting ?? [];
    if (!waiting.length)
        return { enc: after, lost: [] };
    const active = after.running ? after.combatants[after.activeIndex] : undefined;
    const gone = (w) => {
        if (!after.running || !after.combatants.some((c) => c.id === w.id))
            return true;
        if (!rule || rule.until === 'pass')
            return after.round !== w.round || (w.pass !== undefined && after.passes?.pass !== w.pass);
        return active?.id === w.id;
    };
    const lost = waiting.filter(gone).map((w) => w.id);
    return lost.length ? { enc: { ...after, waiting: waiting.filter((w) => !lost.includes(w.id)) }, lost } : { enc: after, lost };
}
/** The reducer's half for these kinds. `encounter.delay` is applied as accepted (the server checked it). */
export function reduceDelay(enc, body) {
    const out = (() => {
        switch (body.kind) {
            case 'encounter.delay': return delayTurn(enc, body.id);
            case 'encounter.act': return body.rule ? actNow(enc, body.id, body.rule) : enc;
            case 'encounter.waitLost': return { ...enc, waiting: (enc.waiting ?? []).filter((w) => !body.ids.includes(w.id)) };
        }
    })();
    // Nobody waiting is no list at all, as a fresh encounter has none (a replay and a snapshot read the same).
    if (out.waiting && !out.waiting.length) {
        const { waiting: _w, ...rest } = out;
        return rest;
    }
    return out;
}
/** The waiting list as a player is sent it: only the combatants they can see, nothing of when the rest stepped out. */
export function waitingSeen(waiting, seen) {
    const out = (waiting ?? []).filter((w) => seen(w.id)).map((w) => ({ ...w }));
    return out.length ? out : undefined;
}
/** A combatant's line in the log: *Mira delays.* / *Mira acts now, before Goblin.* / *Mira's delayed action is lost.* */
export function delayLine(body, name, due) {
    if (body.kind === 'encounter.delay')
        return `${name(body.id)} delays.`;
    if (body.kind === 'encounter.act')
        return due ? `${name(body.id)} acts now, before ${due}.` : `${name(body.id)} acts now.`;
    return body.ids.map((id) => `${name(id)}'s delayed action is lost.`).join(' ');
}
/** A stored waiting list, strictly: each of a combatant still in the fight, once. For `normaliseEncounter`. */
export function normaliseWaiting(v, combatants) {
    if (!Array.isArray(v))
        return undefined;
    const out = [];
    for (const w of v) {
        if (!isObj(w) || typeof w.id !== 'string' || !combatants.some((c) => c.id === w.id) || out.some((x) => x.id === w.id))
            continue;
        if (typeof w.round !== 'number' || !Number.isInteger(w.round) || w.round < 1)
            continue;
        out.push({ id: w.id, round: w.round, ...(typeof w.pass === 'number' && Number.isInteger(w.pass) && w.pass >= 1 ? { pass: w.pass } : {}) });
    }
    return out.length ? out : undefined;
}
//# sourceMappingURL=delay.js.map